import { GUID } from "@faro-lotv/ielement-types";
import { createSelector } from "@reduxjs/toolkit";
import {
  findAnalysis,
  PointCloudAnalysis,
  type PointCloudAnalysisToolState,
} from "./point-cloud-analysis-tool-slice";
import { curryAppSelector } from "./reselect";
import { RootState } from "./store";

/**
 *  @returns the list of analyses associated to a point cloud element
 *  @param id The id of the element which analyses we want to get
 */
export function selectPointCloudAnalyses(id: GUID | undefined) {
  return (state: RootState): PointCloudAnalysis[] => {
    if (!id) return [];

    return id in state.pointCloudAnalysisTool.analyses
      ? state.pointCloudAnalysisTool.analyses[id]
      : [];
  };
}

/**
 *  @returns the analysis matching a given id
 *  @param id The id of the analysis to get
 */
export function selectAnalysis(id: GUID) {
  return (state: RootState): PointCloudAnalysis | undefined =>
    findAnalysis(state.pointCloudAnalysisTool.analyses, id);
}

type PointCloudAndAnalysis = {
  pointCloudId?: GUID;
  activeAnalysis?: PointCloudAnalysis;
};

/**
 * @returns the point cloud and analysis matching a given analysis id
 * @param state The Root State of the app store
 * @param id The id of the analysis to get along with its associated point cloud
 */
export const selectAnalysisAndPointCloud = curryAppSelector(
  createSelector(
    [
      (state: RootState) => state.pointCloudAnalysisTool,
      (state: RootState, id: GUID | undefined) => id,
    ],
    computeAnalysisAndPointCloud,
  ),
);

/**
 * @returns the point cloud and analysis matching a given analysis id
 * @param pointCloudAnalysisTool The store slice related to the point cloud analysis tool
 * @param id The id of the analysis to get along with its associated point cloud
 */
function computeAnalysisAndPointCloud(
  pointCloudAnalysisTool: PointCloudAnalysisToolState,
  id: GUID | undefined,
): PointCloudAndAnalysis {
  if (!id) {
    return {};
  }
  for (const pointCloud of Object.keys(pointCloudAnalysisTool.analyses)) {
    const analysis = pointCloudAnalysisTool.analyses[pointCloud].find(
      (analysis) => analysis.id === id,
    );
    if (analysis) {
      return {
        pointCloudId: pointCloud,
        activeAnalysis: analysis,
      };
    }
  }
  return {};
}

/**
 * @returns the id of the active analysis or undefined if none
 * @param root The Root State
 */
export function selectActiveAnalysisId(root: RootState): GUID | undefined {
  return root.pointCloudAnalysisTool.activeAnalysisId;
}

/**
 * @returns the id of the last created analysis or undefined if none
 * @param root The Root State
 */
export function selectLastCreatedAnalysisId(root: RootState): GUID | undefined {
  return root.pointCloudAnalysisTool.lastCreatedAnalysisId;
}

/**
 * @returns the active analysis or undefined if none
 * @param root The Root State
 */
export function selectActiveAnalysis(
  root: RootState,
): PointCloudAnalysis | undefined {
  const id = selectActiveAnalysisId(root);
  if (!id) return;
  return selectAnalysis(id)(root);
}

/**
 * @returns the number of analyses taken in this browser session
 * @param root current app state
 */
export function selectNumberOfSessionAnalyses(root: RootState): number {
  return root.pointCloudAnalysisTool.numberOfSessionAnalyses;
}

/**
 *  @returns the dirty flag of the analysis matching a given id
 *  @param id The id of the analysis
 */
export function selectAnalysisIsDirty(id: GUID) {
  return (state: RootState): boolean | undefined =>
    findAnalysis(state.pointCloudAnalysisTool.analyses, id)?.isDirty;
}

/**
 * @returns the active analysis should be shown or not
 * @param root The Root State
 */
export function selectShowActiveAnalysis(root: RootState): boolean {
  return !!root.pointCloudAnalysisTool.showActiveAnalysis;
}
